import { Injectable } from '@angular/core';
import { DBSchema, IDBPDatabase, openDB } from 'idb';
import { StoredHikingRoute, StoredTile } from '../interfaces';

const DB_NAME = 'cicerone-offline';
const ROUTES_STORE = 'hiking-routes';
const TILES_STORE = 'hiking-tiles';
const TILES_ROUTE_INDEX = 'routeId';
const DB_VERSION = 2;

export interface OfflineDB extends DBSchema {
  'hiking-routes': {
    key: number;
    value: StoredHikingRoute;
  };
  'hiking-tiles': {
    key: string;
    value: StoredTile;
    indexes: { routeId: number };
  };
}

@Injectable({ providedIn: 'root' })
export class OfflineRoutesService {
  private dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null;

  /**
   * Single owner of the `cicerone-offline` database. Other services (e.g.
   * OfflineTilesService) reuse this handle rather than opening their own, so
   * the version/upgrade transaction is never raced.
   */
  getDb(): Promise<IDBPDatabase<OfflineDB>> {
    if (!this.dbPromise) {
      this.dbPromise = openDB<OfflineDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(ROUTES_STORE)) {
            db.createObjectStore(ROUTES_STORE, { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains(TILES_STORE)) {
            const tilesStore = db.createObjectStore(TILES_STORE, { keyPath: 'key' });
            tilesStore.createIndex(TILES_ROUTE_INDEX, 'routeId');
          }
        },
      });
    }
    return this.dbPromise;
  }

  async put(route: StoredHikingRoute): Promise<void> {
    const db = await this.getDb();
    await db.put(ROUTES_STORE, route);
  }

  async get(id: number): Promise<StoredHikingRoute | undefined> {
    const db = await this.getDb();
    return db.get(ROUTES_STORE, id);
  }

  async getAll(): Promise<StoredHikingRoute[]> {
    const db = await this.getDb();
    return db.getAll(ROUTES_STORE);
  }

  async delete(id: number): Promise<void> {
    const db = await this.getDb();
    await db.delete(ROUTES_STORE, id);
    await this.deleteTilesForRoute(db, id);
  }

  async clear(): Promise<void> {
    const db = await this.getDb();
    await db.clear(ROUTES_STORE);
    await db.clear(TILES_STORE);
  }

  async has(id: number): Promise<boolean> {
    const db = await this.getDb();
    const key = await db.getKey(ROUTES_STORE, id);
    return key !== undefined;
  }

  private async deleteTilesForRoute(db: IDBPDatabase<OfflineDB>, routeId: number): Promise<void> {
    const tx = db.transaction(TILES_STORE, 'readwrite');
    const index = tx.store.index(TILES_ROUTE_INDEX);
    let cursor = await index.openCursor(routeId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  }
}
